// @tennis-matches/services/governance — Serper-Suchergebnisse zu Vereinskandidaten

import {
  isLikelyNonClubDiscoverItem,
  isClubWebsiteHostBlocked,
} from "./superadmin-club-source-trust.js";

const TENNIS_KEYWORDS =
  /\b(tennis|tc\b|tv\b|tsv\b|tsg\b|tennisclub|tennisverein|tennis-club|tennis-verein|padel|turngemeinde|\btg[\s-]|sportverein|djk)\b/i;

function __asStr(v) {
  return String(v ?? "").trim();
}

export function normalizeDiscoverHostname(url) {
  try {
    const raw = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    return new URL(raw).hostname.toLowerCase().replace(/^www\./, "");
  } catch {
    return "";
  }
}

export function normalizeDiscoverWebsiteUrl(url) {
  const trimmed = __asStr(url);
  if (!trimmed) return "";
  try {
    const raw = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    const parsed = new URL(raw);
    if (!/^https?:$/i.test(parsed.protocol)) return trimmed;
    return parsed.origin;
  } catch {
    return trimmed;
  }
}

function __hostBlocked(host) {
  return !host || isClubWebsiteHostBlocked(host);
}

const SERP_PAGE_TITLE_PREFIX =
  /^(mannschafts?-?übersicht|startseite|home|willkommen|tennis|sport|kontakt|über uns|news|aktuell|termine|galerie|impressum|spielplan|ergebnisse|tabellen|turniere?)$/i;

function __isSerpPageTitlePart(text) {
  const t = __asStr(text).toLowerCase();
  if (SERP_PAGE_TITLE_PREFIX.test(t)) return true;
  return /\b(übersicht|mannschaft|spielplan|ergebnisse|tabellen|termine|galerie|news|startseite|kontakt|willkommen)\b/.test(t);
}

function __extractClubNameFromSerpTitle(title) {
  const parts = __asStr(title)
    .split(/\s*[|]\s*/)
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return __asStr(title);

  const clubLike = (text) =>
    /\b(tennisclub|tennisverein|tc |tv |\be\.?\s*v\.?\b)/i.test(text) || /\be\.?\s*v\.?\b/i.test(text);

  if (__isSerpPageTitlePart(parts[0])) {
    const candidate = parts.slice(1).find(clubLike) || parts[parts.length - 1];
    if (candidate && clubLike(candidate)) return candidate;
  }

  return __asStr(title);
}

function __cleanClubTitle(title) {
  let t = __extractClubNameFromSerpTitle(title);
  t = t.replace(/\s*[|\-–—]\s*(Home|Startseite|Willkommen|Start).*$/i, "");
  t = t.replace(/\s*[|\-–—]\s*[^|\-–—]{1,40}$/, (m) => {
    const chunk = m.replace(/^[\s|\-–—]+/, "");
    return chunk.length <= 28 ? m : "";
  });
  return t.slice(0, 140);
}

function __isRelevantOrganicItem(item) {
  const link = __asStr(item?.link || item?.url);
  const title = __asStr(item?.title);
  const snippet = __asStr(item?.snippet);
  const host = normalizeDiscoverHostname(link);
  if (!host || !host.endsWith(".de")) return false;
  if (__hostBlocked(host)) return false;
  if (isLikelyNonClubDiscoverItem(item)) return false;
  const text = `${title} ${snippet} ${link}`;
  if (!TENNIS_KEYWORDS.test(text)) return false;
  return true;
}

/**
 * @param {{ name?: string, type?: string, state?: string }} district
 * @returns {string[]}
 */
export { buildDiscoverSearchQueries } from "./superadmin-club-discover.queries.js";

/**
 * @param {Array<{ title?: string, link?: string, url?: string, snippet?: string }>} organicResults
 * @param {{ kreisName?: string, kreisAgs?: string }} [meta]
 */
export function parseSerperOrganicToClubCandidates(organicResults, meta = {}) {
  const seen = new Set();
  /** @type {Array<{ clubName: string, websiteUrl: string, city: string, sourceUrl: string, kreisAgs: string, kreisName: string }>} */
  const out = [];
  for (const item of Array.isArray(organicResults) ? organicResults : []) {
    if (!__isRelevantOrganicItem(item)) continue;
    const sourceUrl = __asStr(item.link || item.url);
    const websiteUrl = normalizeDiscoverWebsiteUrl(sourceUrl);
    const host = normalizeDiscoverHostname(websiteUrl);
    if (!host || seen.has(host)) continue;
    seen.add(host);
    const clubName = __cleanClubTitle(item.title) || host;
    if (isLikelyNonClubDiscoverItem({ title: clubName, snippet: item?.snippet, link: sourceUrl })) {
      continue;
    }
    out.push({
      clubName,
      websiteUrl,
      city: "",
      sourceUrl,
      sourceSnippet: __asStr(item?.snippet),
      kreisAgs: __asStr(meta.kreisAgs),
      kreisName: __asStr(meta.kreisName),
    });
  }
  return out;
}

/** @param {Array<Array<{ websiteUrl?: string }>>} lists */
export function mergeDiscoverCandidates(...lists) {
  const seen = new Set();
  /** @type {Array<{ clubName: string, websiteUrl: string, city: string, sourceUrl: string, kreisAgs: string, kreisName: string }>} */
  const out = [];
  for (const list of lists) {
    for (const item of Array.isArray(list) ? list : []) {
      const host = normalizeDiscoverHostname(item?.websiteUrl);
      if (!host || seen.has(host)) continue;
      seen.add(host);
      out.push(item);
    }
  }
  return out;
}
